import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, Loader2, PackageOpen, Sparkles } from 'lucide-react';
import { menusAPI } from '../services/api';
import html2pdf from 'html2pdf.js';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import AIAssistModal from '../components/AIAssistModal';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_EVENT_TYPES = {
  onsite: { name: 'Onsite', color: '#9333ea' },
  offsite: { name: 'Offsite', color: '#dc2626' },
  tournament: { name: 'Tournament', color: '#16a34a' },
  cocktail_coach: { name: 'Cocktail Coach', color: '#0abab5' },
  pickup: { name: 'Catering Pick Up', color: '#1e3a5f' },
  dropoff: { name: 'Catering Drop Off', color: '#1e3a5f' },
  celebration_of_life: { name: 'Celebration of Life', color: '#7c3aed' },
  bridal_shower: { name: 'Bridal Shower', color: '#ec4899' },
  baby_shower: { name: 'Baby Shower', color: '#f59e0b' },
  birthday: { name: 'Birthday', color: '#f97316' },
  rehearsal_dinner: { name: 'Rehearsal Dinner', color: '#8b5cf6' },
  golf_tournament: { name: 'Golf Tournament', color: '#059669' },
  rustic_ranch: { name: 'Rustic Ranch', color: '#dc2626' },
};

const DIETARY_LABELS = {
  dietary_vegan: 'Vegan',
  dietary_vegetarian: 'Vegetarian',
  dietary_gluten_free: 'Gluten-Free',
  dietary_nut_allergy: 'Nut Allergy',
  dietary_dairy_free: 'Dairy-Free',
  dietary_halal: 'Halal',
  dietary_kosher: 'Kosher',
};

const BEO = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contract = location.state?.contract || location.state?.beo;
  const isStandalone = !!location.state?.beo;
  const [selectedMenusData, setSelectedMenusData] = useState([]);
  const [dietaryRollup, setDietaryRollup] = useState(null);
  const [eventTypesMap, setEventTypesMap] = useState(FALLBACK_EVENT_TYPES);
  const [loading, setLoading] = useState(true);
  const [showAIBrief, setShowAIBrief] = useState(false);
  const contentRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!contract) {
      navigate(isStandalone ? '/standalone-beos' : '/contracts');
      return;
    }
    setSelectedMenusData([]);
    setDietaryRollup(null);
    setLoading(true);
    fetchMenus();
    fetchDietaryRollup();
    fetchEventTypes();
  }, [contract?.id, navigate]);

  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API}/api/event-types`);
      const map = { ...FALLBACK_EVENT_TYPES };
      (res.data || []).forEach(t => { map[t.slug] = { name: t.name, color: t.color }; });
      setEventTypesMap(map);
    } catch (error) {
      console.warn('Event types fetch failed:', error);
    }
  };

  const fetchMenus = async () => {
    try {
      const allMenus = await menusAPI.getAll();
      const contractMenus = allMenus.filter(m => contract.selected_menus?.includes(m.id));
      setSelectedMenusData(contractMenus);
    } catch (error) {
      console.error('Error loading menus:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDietaryRollup = async () => {
    if (!contract?.event_date || isStandalone) return;
    try {
      const res = await axios.get(`${API}/api/contracts/dietary-by-date/${contract.event_date}`);
      setDietaryRollup(res.data);
    } catch (error) {
      console.error('Error loading dietary rollup:', error);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = () => {
    const element = contentRef.current;
    const opt = {
      margin: 0.3,
      filename: `BEO_${contract.event_name}_${contract.event_date}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
    toast({ title: 'BEO PDF Downloaded!' });
  };

  const handleMakePackingList = () => navigate('/packing', { state: { contract, fromBEO: true } });
  const handleBack = () => navigate(isStandalone ? '/standalone-beos' : '/contracts');

  const formatDateWithDay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayNum = date.getDate();
    const suffix = (dayNum === 1 || dayNum === 21 || dayNum === 31) ? 'ST' :
                   (dayNum === 2 || dayNum === 22) ? 'ND' :
                   (dayNum === 3 || dayNum === 23) ? 'RD' : 'TH';
    return `${dayName}, ${monthName} ${dayNum}${suffix}`;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour12 = h % 12 || 12;
    return minutes === '00' ? `${hour12} ${ampm}` : `${hour12}:${minutes} ${ampm}`;
  };

  // BEO tier renderer: label + qty only (no price, per user requirement)
  const renderTiersNoPrice = (tiers) => {
    const valid = (tiers || []).filter(t => (parseInt(t.quantity) || 0) > 0 || (t.label || '').trim());
    if (valid.length === 0) return null;
    return (
      <div className="pl-3 mt-1 text-sm leading-snug">
        {valid.map((t, i) => (
          <p key={i} className="leading-snug">
            <strong>{parseInt(t.quantity) || 0}</strong> {t.label || '—'}
          </p>
        ))}
      </div>
    );
  };

  const getServiceType = () => {
    if (contract.service_style === 'buffet') return 'Dinner Buffet';
    if (contract.service_style === 'plated') return 'Plated Dinner';
    if (contract.service_style === 'breakfast_buffet') return 'Breakfast Buffet';
    if (contract.service_style === 'lunch_buffet') return 'Lunch Buffet';
    if (contract.service_style === 'plated_lunch') return 'Plated Lunch';
    if (contract.service_style === 'dinner_buffet') return 'Dinner Buffet';
    if (contract.service_style === 'family_style') return 'Family Style';
    if (contract.service_style === 'stations') return 'Food Stations';
    if (contract.service_style === 'stationed_reception') return 'Stationed Reception';
    if (contract.event_type === 'cocktail_coach') return 'Cocktail Coach';
    return 'Dinner Buffet';
  };

  const getTableSetup = () => {
    // Off-site / pickup / drop-off flows have no in-house room setup — the
    // "table style" field is repurposed to declare the delivery mode so the
    // BEO doesn't misleadingly say "Banquet Style" for a drop-off.
    const style = (contract.table_style || '').toLowerCase();
    if (style === 'offsite' || contract.event_type === 'offsite') return 'Off-Site Event';
    if (style === 'catering_pickup' || contract.event_type === 'pickup') return 'Catering Pick Up';
    if (style === 'catering_dropoff' || contract.event_type === 'dropoff') return 'Catering Drop Off';
    if (!contract.table_style) return 'Banquet Style';
    if (style.includes('round')) return `Banquet Rounds of 8`;
    if (style.includes('banquet')) return 'Banquet Tables';
    return contract.table_style;
  };

  const getStations = () => {
    const stations = [];
    if (contract.water_station) stations.push('Water');
    if (contract.coffee_station) stations.push('Coffee');
    if (contract.bar_setup) stations.push('Bar');
    return stations;
  };

  const getSetupItems = () => {
    const items = [];
    if (contract.head_table) items.push('Head Table');
    if (contract.dj_table) items.push('DJ Table');
    if (contract.gift_table) items.push('Gift Table');
    if (contract.cake_table) items.push('Cake Table');
    if (contract.registration_table) items.push('Registration Table');
    if (contract.dessert_station) items.push('Dessert Station');
    if (contract.water_station) items.push('Water Station');
    if (contract.salad_station) items.push('Salad Station');
    if (contract.tournament_setup) items.push('Tournament Set-Up');
    if (contract.prize_tables) items.push('Prize Tables');
    if (contract.see_floorplan) items.push('See Floorplan');
    if (contract.buffet_light_up_wall) items.push('Buffet on Light Up Wall');
    if (contract.buffet_front_of_room) items.push('Buffet Front of Room');
    if (contract.dessert_bay_window) items.push('Dessert Round in Bay Window');
    if (contract.cocktail_tables) items.push('Cocktail Tables');
    if (contract.podium) items.push('Podium');
    if (contract.microphone) items.push('Microphone');
    return items;
  };

  const getAllMenuItems = () => {
    const items = [];
    // Only include items from menus that are ACTUALLY dinner menus (skip
    // cocktail, hors d'oeuvres, and late night packages — those have their
    // own sections and should not spill into the dinner-service fallback).
    selectedMenusData.forEach(menu => {
      const cat = (menu.category || '').toLowerCase();
      const isNonDinner = /hors|cocktail|appetizer|late\s*night/i.test(cat);
      if (!isNonDinner && menu.items) menu.items.forEach(item => items.push(item.name));
    });
    if (contract.selected_buffet?.length > 0) {
      contract.selected_buffet.forEach(item => items.push(item.name));
    }
    // NOTE: `selected_hors_doeuvres` is intentionally NOT included here — those
    // items have their own COCKTAIL HOUR section on the BEO. Including them
    // would cause them to duplicate into the DINNER SERVICE fallback below.
    if (contract.selected_plated_salad) items.push(contract.selected_plated_salad.name);
    if (contract.selected_plated_appetizer) items.push(contract.selected_plated_appetizer.name);
    if (contract.selected_plated_mains?.length > 0) {
      contract.selected_plated_mains.forEach(m => items.push(m.item?.name || m.name));
    }
    if (contract.selected_plated_dessert) items.push(contract.selected_plated_dessert.name);
    return [...new Set(items)];
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  const menuItems = getAllMenuItems();
  const stations = getStations();
  const setupItems = getSetupItems();

  // Check if we have plated selections with quantities
  const hasPlatedSelections = contract.service_style === 'plated' && 
    (contract.plated_starters || contract.plated_mains || contract.plated_desserts || contract.plated_beverages);

  const isPickup = contract.event_type === 'pickup';
  const isDropoff = contract.event_type === 'dropoff';

  return (
    <div className="p-4 bg-[#f5f5dc] min-h-screen print:bg-white print:p-0" data-testid="beo-page">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <Button onClick={handleBack} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
          <ArrowLeft size={18} className="mr-1" />
          {isStandalone ? 'Back' : 'Back'}
        </Button>
        <div className="flex gap-2">
          {contract?.id && !isStandalone && (
            <Button onClick={() => setShowAIBrief(true)} data-testid="ai-brief-btn" className="bg-amber-500 hover:bg-amber-600 text-white text-sm">
              <Sparkles size={16} className="mr-1" />AI Brief
            </Button>
          )}
          <Button onClick={handleMakePackingList} className="bg-[#8b7355] text-white text-sm">
            <PackageOpen size={16} className="mr-1" />Packing List
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-[#1e3a5f] text-white text-sm">
            <Download size={16} className="mr-1" />PDF
          </Button>
          <Button onClick={handlePrint} className="bg-[#c9a227] text-[#1e3a5f] text-sm font-semibold">
            <Printer size={16} className="mr-1" />Print
          </Button>
        </div>
      </div>

      {/* BEO Content - Compact single-page format */}
      <div ref={contentRef} className="bg-white max-w-2xl mx-auto px-6 py-4 shadow-lg print:shadow-none print:max-w-none print:px-8 print:py-2">
        
        {/* Header */}
        {(() => {
          const et = eventTypesMap[contract.event_type] || { name: contract.event_type?.replace(/_/g, ' ') || 'Event', color: '#6b7280' };
          const timeLabel = isPickup ? 'Pick-Up Time' : isDropoff ? 'Drop-Off Time' : 'Time';
          // Detect "this BEO has been updated after its original creation" — used
          // to prompt kitchen/service that they're looking at a revised copy.
          // 60-second buffer so a normal save-immediately-after-create doesn't trip it.
          const createdMs = contract.created_at ? new Date(contract.created_at).getTime() : 0;
          const updatedMs = contract.updated_at ? new Date(contract.updated_at).getTime() : 0;
          const isUpdated = createdMs && updatedMs && (updatedMs - createdMs) > 60_000;
          return (
            <>
              {isUpdated && (
                <div
                  className="text-center mb-2 py-1 border-2 border-red-600 bg-red-50 print:bg-white"
                  data-testid="beo-updated-banner"
                >
                  <span className="text-lg font-extrabold tracking-widest text-red-700 uppercase">
                    UPDATED
                  </span>
                  <span className="ml-2 text-xs text-red-700">
                    (revised {new Date(contract.updated_at).toLocaleDateString()})
                  </span>
                </div>
              )}
              <div className="text-center border-b-2 border-black pb-2 mb-2" style={{ borderBottomColor: et.color }}>
                <h1 className="text-2xl font-bold tracking-wide" data-testid="beo-day-header">
                  {formatDateWithDay(contract.event_date)}
                </h1>
                <p className="text-sm font-semibold text-gray-600">BANQUET EVENT ORDER</p>
                <div className="mt-1 flex items-center justify-center gap-2">
                  <span
                    className="inline-block text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: et.color }}
                    data-testid="beo-event-type-badge"
                  >
                    {et.name}
                  </span>
                </div>
              </div>

              {/* Event Info - Compact 2-column layout */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-3 border-b pb-2">
                <div><span className="font-semibold">Event:</span> {contract.event_name}</div>
                <div><span className="font-semibold">Guests:</span> {contract.guest_count}</div>
                <div>
                  <span className="font-semibold">{timeLabel}:</span> {formatTime(contract.event_time)}
                </div>
                {!isPickup && !isDropoff && (
                  <div><span className="font-semibold">Service:</span> {getServiceType()}</div>
                )}
                {contract.room && (() => {
                  // Friendly labels — otherwise `catering_dropoff` renders as "Catering Dropoff".
                  const ROOM_LABELS = {
                    catering_pickup: 'Catering Pick Up',
                    catering_dropoff: 'Catering Drop Off',
                    offsite: 'Offsite',
                    banquet_hall: 'Banquet Hall',
                    lower_patio: 'Lower Patio',
                    upper_patio: 'Upper Patio',
                    ceremony_space: 'Ceremony Space',
                    pub: 'Pub',
                  };
                  const label = ROOM_LABELS[contract.room] || contract.room.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (<div data-testid="beo-room"><span className="font-semibold">Room:</span> {label}</div>);
                })()}
                {!isPickup && !isDropoff && (
                  <div><span className="font-semibold">Setup:</span> {getTableSetup()}</div>
                )}
                {contract.tablecloth_color && !isPickup && !isDropoff && <div><span className="font-semibold">Linen:</span> {contract.tablecloth_color}{contract.napkin_color ? ` / ${contract.napkin_color} napkins` : ''}</div>}
                {contract.bar_type && <div><span className="font-semibold">Bar:</span> {contract.bar_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>}
                {stations.length > 0 && <div><span className="font-semibold">Stations:</span> {stations.join(', ')}</div>}
                {setupItems.length > 0 && !isPickup && !isDropoff && <div><span className="font-semibold">Tables:</span> {setupItems.join(', ')}</div>}
                {contract.location && (contract.location_type === 'offsite' || isDropoff) && (
                  <div className="col-span-2">
                    <span className="font-semibold">{isDropoff ? 'Drop-Off Address' : 'Location'}:</span> {contract.location}
                  </div>
                )}
                {isDropoff && (contract.delivery_contact_name || contract.delivery_contact_phone) && (
                  <div className="col-span-2 bg-blue-50 border border-blue-200 rounded px-3 py-2 mt-1" data-testid="beo-delivery-contact">
                    <span className="text-xs font-bold uppercase tracking-wide text-blue-900">Delivery Contact: </span>
                    {contract.delivery_contact_name && <span className="text-sm font-semibold">{contract.delivery_contact_name}</span>}
                    {contract.delivery_contact_phone && <span className="text-sm font-semibold"> · {contract.delivery_contact_phone}</span>}
                  </div>
                )}
                {isPickup && (
                  <div className="col-span-2 bg-[#1e3a5f]/5 rounded px-2 py-1 mt-1">
                    <span className="font-semibold text-[#1e3a5f]">Pick-Up Location:</span> Willow Ridge Golf Club
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {/* Visual Event Timeline Bar */}
        {(() => {
          const steps = [
            { time: contract.event_start_time, label: 'Arrival', icon: '>' },
            { time: contract.breakfast_time, label: 'Breakfast', icon: '' },
            { time: contract.brunch_time, label: 'Brunch', icon: '' },
            { time: contract.lunch_time, label: 'Lunch', icon: '' },
            { time: contract.cocktail_hour_time, label: 'Cocktails', icon: '' },
            { time: contract.dinner_start_time, label: 'Dinner', icon: '' },
            { time: contract.late_night_time, label: 'Late Night', icon: '' },
            { time: contract.event_end_time, label: 'End', icon: '' },
          ].filter(t => t.time);
          if (steps.length < 2) return null;
          return (
            <div className="mb-3 border-b pb-3" data-testid="beo-timeline-bar">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-300" style={{zIndex:0}} />
                {steps.map((s, i) => (
                  <div key={i} className="flex flex-col items-center relative" style={{zIndex:1}}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${i === 0 ? 'bg-[#1e3a5f] text-white' : i === steps.length - 1 ? 'bg-gray-800 text-white' : 'bg-[#c9a227] text-white'}`}>
                      {i + 1}
                    </div>
                    <span className="text-[10px] font-bold mt-1 text-center leading-tight">{s.label}</span>
                    <span className="text-[9px] text-gray-600">{formatTime(s.time)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Menu Section - Organized by service phase */}
        <div className="mb-3">

          {/* Custom Menu */}
          {contract.custom_menu && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">
                  {(contract.event_type === 'pickup' || contract.event_type === 'dropoff') ? 'MENU' : 'CUSTOM MENU NOTES'}
                </span>
              </div>
              <div className="pl-3 whitespace-pre-line text-sm leading-snug">
                {contract.custom_menu}
              </div>
            </div>
          )}

          {/* ═══ SERVICE SECTIONS ═══ */}
          {/* Grouped by service phase: Breakfast → Lunch → Cocktail Hour → Dinner → Dessert → Late Night → On the Turn → Bar */}

          {/* Breakfast (custom) */}
          {(contract.custom_menu_breakfast || (contract.custom_menu_breakfast_tiers || []).length > 0) && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">BREAKFAST</span>
                {contract.breakfast_time && <span className="text-sm ml-2">• {formatTime(contract.breakfast_time)}</span>}
              </div>
              {contract.custom_menu_breakfast && (
                <div className="pl-3 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_breakfast}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_breakfast_tiers)}
            </div>
          )}

          {/* Lunch (custom) */}
          {(contract.custom_menu_lunch || (contract.custom_menu_lunch_tiers || []).length > 0) && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">LUNCH</span>
                {contract.lunch_time && <span className="text-sm ml-2">• {formatTime(contract.lunch_time)}</span>}
              </div>
              {contract.custom_menu_lunch && (
                <div className="pl-3 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_lunch}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_lunch_tiers)}
            </div>
          )}

          {/* Cocktail Hour (Hors d'Oeuvres + Cocktail-category menus) */}
          {(() => {
            const hdItems = contract.selected_hors_doeuvres || [];
            const cocktailMenus = selectedMenusData.filter(m => /hors|cocktail|appetizer/i.test(m.category));
            if (hdItems.length === 0 && cocktailMenus.length === 0 && !contract.custom_menu_cocktail && (contract.custom_menu_cocktail_tiers || []).length === 0) return null;
            return (
              <div className="mb-2">
                <div className="bg-black text-white py-1 px-3 mb-1">
                  <span className="text-sm font-bold tracking-wide">COCKTAIL HOUR</span>
                  {contract.cocktail_hour_time && <span className="text-sm ml-2">• {formatTime(contract.cocktail_hour_time)}</span>}
                  {contract.hors_doeuvres_service && <span className="text-sm ml-2">• {contract.hors_doeuvres_service === 'passed' ? 'Passed' : 'Stationed'}</span>}
                </div>
                <div className="pl-3 columns-2 gap-x-4">
                  {/* Show the user's explicit hors d'oeuvres picks; only fall back
                      to dumping ALL items from a selected menu package when the
                      user has NOT chosen any specific items themselves. */}
                  {hdItems.length > 0 ? (
                    hdItems.map((item, idx) => (
                      <p key={`hd-${idx}`} className="text-sm leading-snug">{item.name}</p>
                    ))
                  ) : (
                    cocktailMenus.map(menu => menu.items?.map((item, idx) => (
                      <p key={`cm-${menu.id}-${idx}`} className="text-sm leading-snug">{item.name}</p>
                    )))
                  )}
                </div>
                {contract.custom_menu_cocktail && (
                  <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_cocktail}</div>
                )}
                {renderTiersNoPrice(contract.custom_menu_cocktail_tiers)}
              </div>
            );
          })()}

          {/* Dinner Service — menus that are NOT cocktail or late night */}
          {(() => {
            const dinnerMenus = selectedMenusData.filter(m => !/hors|cocktail|appetizer|late\s*night/i.test(m.category));
            const hasPlated = hasPlatedSelections;
            const hasBuffet = contract.selected_buffet?.length > 0;
            const hasPlatedSingles =
              contract.selected_plated_appetizer || contract.selected_plated_salad ||
              contract.selected_plated_mains?.length > 0 || contract.selected_plated_dessert;
            const hasFallback = menuItems && menuItems.length > 0;
            const hasAnyFood = hasPlated || hasBuffet || dinnerMenus.length > 0 || hasPlatedSingles || hasFallback || contract.custom_menu_dinner || (contract.custom_menu_dinner_tiers || []).length > 0;
            if (!hasAnyFood) return null;
            return (
          <div className="mb-2">
            <div className="bg-black text-white py-1 px-3 mb-1">
              <span className="text-sm font-bold tracking-wide">DINNER SERVICE</span>
              {contract.dinner_start_time && <span className="text-sm ml-2">• {formatTime(contract.dinner_start_time)}</span>}
              {!isPickup && !isDropoff && <span className="text-sm ml-2">• {getServiceType()}</span>}
              {hasBuffet && contract.chafing_dish_count ? <span className="text-sm ml-2">• {contract.chafing_dish_count} chafing dish{contract.chafing_dish_count === 1 ? '' : 'es'}</span> : null}
            </div>

            {/* Plated Menu with quantities */}
            {hasPlatedSelections && (
              <div className="pl-3">
                {[
                  { data: contract.plated_starters, label: 'Starters' },
                  { data: contract.plated_mains, label: 'Mains' },
                  { data: contract.plated_desserts, label: 'Desserts' },
                  { data: contract.plated_beverages, label: 'Beverages' },
                ].filter(s => s.data && Object.keys(s.data).length > 0).map(section => (
                  Object.entries(section.data).filter(([_, v]) => v.quantity > 0).map(([id, item]) => (
                    <p key={id} className="text-sm leading-snug"><strong>{item.quantity}</strong> {item.name}</p>
                  ))
                ))}
              </div>
            )}

            {/* Non-plated items */}
            {(!hasPlatedSelections || Object.keys(contract.plated_mains || {}).length === 0) && (
              <div className="pl-3 columns-2 gap-x-4">
                {contract.selected_buffet?.map((item, idx) => (
                  <p key={`b-${idx}`} className="text-sm leading-snug">{item.name}</p>
                ))}
                {/* Menu-package dump: ONLY when the user hasn't picked any
                    specific dinner items themselves — otherwise we'd print the
                    entire menu on top of their real selections. */}
                {!contract.selected_buffet?.length
                  && !contract.selected_plated_appetizer
                  && !contract.selected_plated_salad
                  && !contract.selected_plated_mains?.length
                  && !contract.selected_plated_dessert
                  && Object.keys(contract.plated_mains || {}).length === 0
                  && dinnerMenus.map(menu =>
                    menu.items?.map((item, idx) => (
                      <p key={`m-${menu.id}-${idx}`} className="text-sm leading-snug">{item.name}</p>
                    ))
                )}
                {contract.selected_plated_appetizer && <p className="text-sm leading-snug">{contract.selected_plated_appetizer.name}</p>}
                {contract.selected_plated_salad && <p className="text-sm leading-snug">{contract.selected_plated_salad.name}</p>}
                {contract.selected_plated_mains?.map((m, idx) => (
                  <p key={`pm-${idx}`} className="text-sm leading-snug"><strong>{m.quantity}</strong> {m.item?.name || m.name}</p>
                ))}
                {contract.selected_plated_dessert && <p className="text-sm leading-snug">{contract.selected_plated_dessert.name}</p>}
                {!contract.selected_buffet?.length && dinnerMenus.length === 0 && !contract.selected_plated_mains?.length && menuItems.map((item, idx) => (
                  <p key={`f-${idx}`} className="text-sm leading-snug">{item}</p>
                ))}
              </div>
            )}
            {contract.custom_menu_dinner && (
              <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_dinner}</div>
            )}
            {renderTiersNoPrice(contract.custom_menu_dinner_tiers)}
          </div>
            );
          })()}

          {/* Dessert (custom) */}
          {(contract.custom_menu_dessert || (contract.custom_menu_dessert_tiers || []).length > 0) && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">DESSERT</span>
              </div>
              {contract.custom_menu_dessert && (
                <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_dessert}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_dessert_tiers)}
            </div>
          )}

          {/* Coffee / Tea — pulled from the Club Services Roll-Up so the driver
              and kitchen see identical info without duplicating notes. */}
          {(contract.coffee_service === true
            || contract.coffee_notes
            || (contract.coffee_decanters != null && contract.coffee_decanters > 0)
          ) && (
            <div className="mb-2" data-testid="beo-coffee-tea">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">COFFEE / TEA</span>
              </div>
              <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">
                {contract.coffee_notes
                  || (contract.coffee_decanters ? `${contract.coffee_decanters} decanter${contract.coffee_decanters === 1 ? '' : 's'}` : 'Coffee & tea service')}
              </div>
            </div>
          )}

          {/* Late Night (selectable items + Late Night category menus) */}
          {(() => {
            const lnItems = contract.selected_late_night || [];
            const lnMenus = selectedMenusData.filter(m => /late\s*night/i.test(m.category));
            if (lnItems.length === 0 && lnMenus.length === 0 && !contract.custom_menu_late_night && (contract.custom_menu_late_night_tiers || []).length === 0) return null;
            return (
              <div className="mb-2">
                <div className="bg-black text-white py-1 px-3 mb-1">
                  <span className="text-sm font-bold tracking-wide">LATE NIGHT</span>
                  {contract.late_night_time && <span className="text-sm ml-2">• {formatTime(contract.late_night_time)}</span>}
                </div>
                <div className="pl-3 columns-2 gap-x-4">
                  {/* Show the user's explicit late-night picks; only fall back
                      to dumping ALL items from a selected menu package when the
                      user has NOT chosen any specific items themselves. */}
                  {lnItems.length > 0 ? (
                    lnItems.map((item, idx) => (
                      <p key={`ln-${idx}`} className="text-sm leading-snug">{item.name}</p>
                    ))
                  ) : (
                    lnMenus.map(menu => (
                      <React.Fragment key={`lnm-${menu.id}`}>
                        <p className="text-sm font-semibold col-span-2 mt-1">{menu.name}</p>
                        {menu.items?.map((item, idx) => (
                          <p key={`lnmi-${menu.id}-${idx}`} className="text-sm leading-snug">{item.name}</p>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </div>
                {contract.custom_menu_late_night && (
                  <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_late_night}</div>
                )}
                {renderTiersNoPrice(contract.custom_menu_late_night_tiers)}
              </div>
            );
          })()}

          {/* On the Turn (Golf Tournaments) */}
          {(contract.selected_on_the_turn?.length > 0 || contract.custom_menu_on_the_turn || (contract.custom_menu_on_the_turn_tiers || []).length > 0) && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">ON THE TURN</span>
                <span className="text-xs ml-2 opacity-75">(tax included)</span>
              </div>
              <div className="pl-3">
                {(contract.selected_on_the_turn || []).map((item, idx) => (
                  <p key={idx} className="text-sm leading-snug">{item.name} — ${(item.price_per_person || item.price || 0).toFixed(2)}</p>
                ))}
              </div>
              {contract.custom_menu_on_the_turn && (
                <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_on_the_turn}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_on_the_turn_tiers)}
            </div>
          )}

          {/* Beverage (custom non-alcoholic / coffee / tea) */}
          {(contract.custom_menu_beverage || (contract.custom_menu_beverage_tiers || []).length > 0) && (
            <div className="mb-2">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">BEVERAGE</span>
              </div>
              {contract.custom_menu_beverage && (
                <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_beverage}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_beverage_tiers)}
            </div>
          )}

          {/* Bar — grouped by sub_category for readability (especially Cocktail Coach) */}
          {contract.selected_bar?.length > 0 && (() => {
            const grouped = {};
            contract.selected_bar.forEach(item => {
              const key = (item.sub_category || 'other').toLowerCase();
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(item);
            });
            // Stable canonical ordering, then alphabetical for unknown keys
            const ORDER = ['beer', 'wine', 'spirits', 'cocktails', 'coolers', 'mix', 'garnish', 'other'];
            const orderedKeys = Object.keys(grouped).sort((a, b) => {
              const ai = ORDER.indexOf(a); const bi = ORDER.indexOf(b);
              if (ai === -1 && bi === -1) return a.localeCompare(b);
              if (ai === -1) return 1;
              if (bi === -1) return -1;
              return ai - bi;
            });
            const prettify = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return (
              <div className="mb-2 mt-3">
                <div className="bg-black text-white py-1 px-3 mb-1">
                  <span className="text-sm font-bold tracking-wide">BAR</span>
                </div>
                <div className="pl-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {orderedKeys.map(key => (
                    <div key={key} className="break-inside-avoid">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-600 border-b border-gray-300 pb-0.5 mb-1">
                        {prettify(key)}
                      </p>
                      <div className="pl-1">
                        {grouped[key].map((item, i) => (
                          <p key={item.id || `${key}-${i}`} className="text-sm leading-snug">{item.name}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {contract.custom_menu_bar && (
                  <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_bar}</div>
                )}
                {renderTiersNoPrice(contract.custom_menu_bar_tiers)}
              </div>
            );
          })()}

          {/* Bar custom notes when no selected_bar items exist */}
          {!(contract.selected_bar?.length > 0) && (contract.custom_menu_bar || (contract.custom_menu_bar_tiers || []).length > 0) && (
            <div className="mb-2 mt-3">
              <div className="bg-black text-white py-1 px-3 mb-1">
                <span className="text-sm font-bold tracking-wide">BAR</span>
              </div>
              {contract.custom_menu_bar && (
                <div className="pl-3 mt-1 whitespace-pre-line text-sm leading-snug">{contract.custom_menu_bar}</div>
              )}
              {renderTiersNoPrice(contract.custom_menu_bar_tiers)}
            </div>
          )}

          {/* Notes — only show when this event has food service */}
          {contract.event_type !== 'cocktail_coach' && (
            <div className="text-xs text-gray-500 mt-1 pl-3 space-x-3">
              {!contract.selected_plated_dessert &&
               !menuItems.some(i => i.toLowerCase().includes('dessert')) &&
               (!contract.plated_desserts || Object.keys(contract.plated_desserts).length === 0) &&
               !contract.custom_menu_dessert &&
               (!contract.custom_menu_dessert_tiers || contract.custom_menu_dessert_tiers.length === 0) && (
                <span className="italic">No Dessert</span>
              )}
              {/* "No Coffee/Tea" was misleading — user records coffee/tea in
                  the Dessert custom-menu notes.  Removed. */}
            </div>
          )}
        </div>

        {/* Staff & Add-ons */}
        {((contract.custom_addons?.length > 0) || (contract.selected_addons?.length > 0)) && (
          <div className="mb-3">
            <div className="bg-black text-white py-1 px-3 mb-2">
              <span className="text-sm font-bold tracking-wide">STAFF & ADD-ONS</span>
            </div>
            {contract.custom_addons?.length > 0 && (
              <div className="pl-4 mb-2">
                {contract.custom_addons.map((addon, idx) => (
                  <div key={idx} className="text-sm mb-1">
                    <span className="font-medium">{addon.name}</span>
                  </div>
                ))}
              </div>
            )}
            {contract.selected_addons?.length > 0 && (
              <div className="pl-4">
                {contract.selected_addons.map((addon, idx) => (
                  <div key={idx} className="text-sm mb-1">
                    <span className="font-medium">{addon.name}</span>
                    {addon.description && <span className="text-gray-600"> — {addon.description}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Special Requests */}
        {contract.special_requests && (
          <div className="mb-3 border-t pt-2">
            <div className="bg-black text-white py-1 px-3 mb-1 inline-block">
              <span className="text-xs font-bold">ORDER NOTE</span>
            </div>
            <p className="text-sm">{contract.special_requests}</p>
          </div>
        )}

        {/* Kitchen Notes - BOH Only */}
        {contract.kitchen_notes && (
          <div className="mb-3 border-2 border-dashed border-amber-500 bg-amber-50 rounded-lg p-3" data-testid="beo-kitchen-notes">
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded">KITCHEN NOTES</span>
              <span className="text-[10px] text-amber-700">BOH ONLY — DO NOT SHARE WITH CLIENT</span>
            </div>
            <p className="text-sm font-medium text-amber-900 whitespace-pre-line">{contract.kitchen_notes}</p>
          </div>
        )}

        {/* Staff Assignment */}
        {contract.assigned_staff?.length > 0 && (
          <div className="mb-3 border rounded-lg p-2" data-testid="beo-staff-assignment">
            <div className="bg-black text-white py-1 px-3 mb-2 inline-block">
              <span className="text-xs font-bold">STAFF ASSIGNMENT</span>
            </div>
            <div className="grid grid-cols-2 gap-1 pl-2">
              {contract.assigned_staff.filter(s => s.name).map((s, i) => (
                <div key={i} className="text-sm"><span className="font-semibold">{s.role}:</span> {s.name}</div>
              ))}
            </div>
          </div>
        )}

        {/* Dietary Requirements — this event + aggregated across all events on this date */}
        {dietaryRollup && (dietaryRollup.total_events > 0) && (() => {
          const totalDietary = Object.values(dietaryRollup.totals).reduce((s, v) => s + v, 0);
          const contractDietary = dietaryRollup.contracts.find(e => e.contract_id === contract.id);
          const hasOwn = contractDietary && Object.keys(DIETARY_LABELS).some(k => (contractDietary[k] || 0) > 0 || !!contractDietary.dietary_other);
          if (!hasOwn && totalDietary === 0) return null;
          return (
            <div className="mb-3 border-t pt-2" data-testid="beo-dietary-section">
              <div className="bg-black text-white py-1 px-3 mb-2">
                <span className="text-sm font-bold tracking-wide">DIETARY REQUIREMENTS</span>
              </div>
              {hasOwn && (
                <div className="pl-3 mb-2">
                  <p className="text-xs font-semibold text-gray-700 mb-1">This Event:</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    {Object.entries(DIETARY_LABELS).map(([key, label]) => {
                      const count = contractDietary?.[key] || 0;
                      if (count === 0) return null;
                      return (
                        <span key={key}>
                          <span className="font-bold">{count}</span> {label}
                        </span>
                      );
                    })}
                  </div>
                  {contractDietary?.dietary_other && (
                    <p className="text-sm mt-1 italic text-gray-700">Notes: {contractDietary.dietary_other}</p>
                  )}
                </div>
              )}
              {dietaryRollup.total_events > 1 && totalDietary > 0 && (
                <div className="pl-3 bg-amber-50 p-2 rounded border border-amber-200">
                  <p className="text-xs font-semibold text-amber-900 mb-1">
                    Kitchen Roll-Up — All {dietaryRollup.total_events} events on this date:
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    {Object.entries(DIETARY_LABELS).map(([key, label]) => {
                      const count = dietaryRollup.totals[key] || 0;
                      if (count === 0) return null;
                      return (
                        <span key={key}>
                          <span className="font-bold">{count}</span> {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer */}
        <div className="pt-2 border-t text-xs text-gray-500 flex justify-between">
          <span>{contract.client_name}</span>
          <span>{contract.client_phone}</span>
        </div>
      </div>

      {showAIBrief && contract?.id && (
        <AIAssistModal mode="brief" contract={contract} onClose={() => setShowAIBrief(false)} />
      )}
    </div>
  );
};

export default BEO;
